import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

type ContactMessageAdminStatus = 'new' | 'in_review' | 'contacted' | 'closed';

type ContactMessageInterest =
  | 'proposal_support'
  | 'capture_management'
  | 'federal_market_research'
  | 'federal_sales_strategy'
  | 'other';

type ContactMessageBestTime = 'morning' | 'afternoon' | 'either';

interface ContactMessageRecord {
  id: string;
  fullName: string;
  email: string;
  companyName: string;
  phone?: string | null;
  interest: ContactMessageInterest;
  message?: string | null;
  bestTime?: ContactMessageBestTime | null;
  source: string;
  adminStatus: ContactMessageAdminStatus;
  adminNotes?: string | null;
  createdAt?: { toDate?: () => Date };
  updatedAt?: { toDate?: () => Date };
}

interface AdminContactMessagesProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const INTEREST_LABELS: Record<ContactMessageInterest, string> = {
  proposal_support: 'Proposal Support',
  capture_management: 'Capture Management',
  federal_market_research: 'Federal Market Research',
  federal_sales_strategy: 'Federal Sales Strategy',
  other: 'Other',
};

const BEST_TIME_LABELS: Record<ContactMessageBestTime, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  either: 'Either',
};

const statusLabel: Record<ContactMessageAdminStatus, string> = {
  new: 'New',
  in_review: 'In Review',
  contacted: 'Contacted',
  closed: 'Closed',
};

const statusColorClass: Record<ContactMessageAdminStatus, string> = {
  new: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  contacted: 'bg-purple-100 text-purple-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

export function AdminContactMessages({ showNotify }: AdminContactMessagesProps): JSX.Element {
  const [messages, setMessages] = useState<ContactMessageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContactMessageAdminStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'contact_messages'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const records = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as ContactMessageRecord[];
        setMessages(records);
        setLoading(false);
      },
      (error) => {
        console.error(
          '[AdminContactMessages] Failed to subscribe to contact_messages. Admin list cannot refresh:',
          error
        );
        showNotify?.('Failed to load strategy call messages.', 'error');
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [showNotify]);

  const filtered = useMemo(() => {
    const norm = search.trim().toLowerCase();
    return messages.filter((r) => {
      const passStatus = statusFilter === 'all' ? true : r.adminStatus === statusFilter;
      if (!passStatus) return false;
      if (!norm) return true;
      const blob = [
        r.fullName,
        r.email,
        r.companyName,
        r.phone ?? '',
        r.message ?? '',
        INTEREST_LABELS[r.interest] ?? r.interest,
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(norm);
    });
  }, [messages, search, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const selected = useMemo(
    () =>
      filtered.find((r) => r.id === selectedId) ?? paginated[0] ?? null,
    [filtered, selectedId, paginated]
  );

  useEffect(() => {
    if (!selected) {
      setAdminNotesDraft('');
      return;
    }
    setAdminNotesDraft(selected.adminNotes ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync notes when selection changes by id only
  }, [selected?.id]);

  const formatDate = (ts: ContactMessageRecord['createdAt']) => {
    if (!ts || typeof ts !== 'object' || typeof (ts as { toDate?: () => Date }).toDate !== 'function')
      return '—';
    return (ts as { toDate: () => Date }).toDate().toLocaleString();
  };

  const handleStatusUpdate = async (nextStatus: ContactMessageAdminStatus) => {
    if (!selected) return;
    setIsUpdating(true);
    try {
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'contact_messages', selected.id),
        {
          adminStatus: nextStatus,
          adminNotes: adminNotesDraft.trim() || null,
          updatedAt: serverTimestamp(),
        }
      );
      showNotify?.(`Message for ${selected.companyName} moved to ${statusLabel[nextStatus]}.`, 'success');
    } catch (error) {
      console.error(
        '[AdminContactMessages] Failed to update message status:',
        selected.id,
        nextStatus,
        error
      );
      showNotify?.('Unable to update status. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <div className="text-gray-600">Loading strategy call messages...</div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Strategy Call Messages</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage submissions from the Book a Strategy Call form.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, company..."
              className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as ContactMessageAdminStatus | 'all')
            }
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="in_review">In Review</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            <span className="font-semibold">{filtered.length}</span> message(s)
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)] gap-4">
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Interest
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-sm text-center text-gray-500">
                      No strategy call messages found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((record) => {
                    const active =
                      (selected?.id ?? selectedId ?? paginated[0]?.id) === record.id;
                    return (
                      <tr
                        key={record.id}
                        onClick={() => setSelectedId(record.id)}
                        className={`cursor-pointer transition-colors ${
                          active ? 'bg-blue-50/70' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 align-top">
                          <p className="text-sm font-semibold text-gray-900">
                            {record.companyName}
                          </p>
                          <p className="text-xs text-gray-600">{record.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">
                          {INTEREST_LABELS[record.interest] ?? record.interest}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusColorClass[record.adminStatus]}`}
                          >
                            {statusLabel[record.adminStatus]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 align-top">
                          {formatDate(record.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
          {selected ? (
            <>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selected.companyName}</h3>
                <p className="text-sm text-gray-600">
                  {selected.fullName} · {selected.email}
                </p>
                {selected.phone && (
                  <p className="text-sm text-gray-600">{selected.phone}</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <p className="font-semibold text-gray-900">Interest</p>
                  <p className="text-gray-700">
                    {INTEREST_LABELS[selected.interest] ?? selected.interest}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Best time</p>
                  <p className="text-gray-700">
                    {selected.bestTime
                      ? BEST_TIME_LABELS[selected.bestTime]
                      : '—'}
                  </p>
                </div>
                {selected.message && (
                  <div>
                    <p className="font-semibold text-gray-900">Message</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">Admin notes</label>
                <textarea
                  rows={4}
                  value={adminNotesDraft}
                  onChange={(e) => setAdminNotesDraft(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Follow-up notes, call outcomes..."
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('in_review')}
                  className="inline-flex items-center justify-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                >
                  Mark In Review
                </button>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('contacted')}
                  className="rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                >
                  Mark Contacted
                </button>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('new')}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Reset to New
                </button>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('closed')}
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Select a message to view details and update status.
            </p>
          )}
        </div>
      </div>

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Showing{' '}
              <span className="font-medium text-gray-900">
                {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-medium text-gray-900">{filtered.length}</span>{' '}
              message{filtered.length !== 1 ? 's' : ''}
            </span>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Rows per page
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page <span className="font-medium text-gray-900">{currentPage}</span> of{' '}
              <span className="font-medium text-gray-900">{totalPages}</span>
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
